/**
 * CREATE LICENSE DIALOG
 *
 * Dialog for super admins to create new licenses for companies
 */

import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Check, Copy, Loader2 } from 'lucide-react';
import { useCreateLicense } from '@/hooks/queries/useAdminLicenses';
import { useLicensePlans } from '@/hooks/queries/useLicensePlans';
import { useAdminCompanies } from '@/hooks/queries/useAdminLicenses';
import type { CreateLicenseRequest } from '@/services/adminLicenseService';

interface CreateLicenseDialogProps {
  open: boolean;
  onClose: () => void;
}

export function CreateLicenseDialog({ open, onClose }: CreateLicenseDialogProps) {
  const [autoGenerateKey, setAutoGenerateKey] = useState(true);
  const [createdLicenseKey, setCreatedLicenseKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: companies, isLoading: loadingCompanies } = useAdminCompanies();
  const { data: plans, isLoading: loadingPlans } = useLicensePlans();
  const createLicense = useCreateLicense();

  const { register, handleSubmit, watch, reset, formState: { errors } } = useForm<CreateLicenseRequest>({
    defaultValues: {
      licenseType: 'hybrid',
      durationMonths: 12,
      maxDevices: 3,
    },
  });

  const selectedLicenseType = watch('licenseType');

  const onSubmit = async (data: CreateLicenseRequest) => {
    try {
      const result = await createLicense.mutateAsync(data);
      setCreatedLicenseKey(result.license_key);
      reset();
    } catch (error) {
      console.error('Failed to create license:', error);
    }
  };

  const handleClose = () => {
    setCreatedLicenseKey(null);
    setCopied(false);
    reset();
    onClose();
  };

  const copyToClipboard = () => {
    if (createdLicenseKey) {
      navigator.clipboard.writeText(createdLicenseKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // If license was just created, show success screen
  if (createdLicenseKey) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-600" />
              License Created Successfully!
            </DialogTitle>
            <DialogDescription>
              Copy this license key and provide it to the customer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="p-4 bg-emerald-50 dark:bg-emerald-950/20 rounded-lg">
              <Label className="text-xs text-muted-foreground">License Key</Label>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 text-sm font-mono bg-white dark:bg-gray-900 px-3 py-2 rounded border">
                  {createdLicenseKey}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyToClipboard}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button onClick={handleClose}>Done</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Otherwise, show create form
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New License</DialogTitle>
          <DialogDescription>
            Issue a new license for a company
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Company Selection */}
          <div className="space-y-2">
            <Label htmlFor="companyId">
              Company <span className="text-red-500">*</span>
            </Label>
            <select
              id="companyId"
              {...register('companyId', { required: 'Company is required' })}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              disabled={loadingCompanies}
            >
              <option value="">Select a company...</option>
              {companies?.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.name} {company.owner ? `- ${company.owner.full_name} (${company.owner.email})` : ''}
                </option>
              ))}
            </select>
            {errors.companyId && (
              <p className="text-sm text-red-500">{errors.companyId.message}</p>
            )}
            {/* Show selected company owner details */}
            {watch('companyId') && companies && (
              (() => {
                const selectedCompany = companies.find(c => c.id === watch('companyId'));
                if (selectedCompany?.owner) {
                  return (
                    <div className="p-3 bg-muted/50 rounded-lg mt-2">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Owner</p>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                          <span className="text-xs font-semibold text-emerald-600">
                            {selectedCompany.owner.full_name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium">{selectedCompany.owner.full_name}</p>
                          <p className="text-xs text-muted-foreground">{selectedCompany.owner.email}</p>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()
            )}
          </div>

          {/* License Plan Selection */}
          <div className="space-y-2">
            <Label htmlFor="planId">
              License Plan <span className="text-red-500">*</span>
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {loadingPlans ? (
                <div className="col-span-2 flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              ) : (
                plans?.map((plan) => (
                  <label
                    key={plan.id}
                    className="relative flex items-start p-4 border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors"
                  >
                    <input
                      type="radio"
                      {...register('planId', { required: 'Plan is required' })}
                      value={plan.id}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">{plan.name}</span>
                        <Badge variant="outline">Tier {plan.tier_level}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mb-2">
                        {plan.max_users} users • {plan.max_invoices_per_month} invoices/month
                      </p>
                      <p className="text-sm font-semibold text-emerald-600">
                        {plan.price_yearly_dzd.toLocaleString('ar-DZ')} DZD/year
                      </p>
                    </div>
                  </label>
                ))
              )}
            </div>
            {errors.planId && (
              <p className="text-sm text-red-500">{errors.planId.message}</p>
            )}
          </div>

          {/* License Type */}
          <div className="space-y-2">
            <Label htmlFor="licenseType">
              License Type <span className="text-red-500">*</span>
            </Label>
            <select
              id="licenseType"
              {...register('licenseType', { required: true })}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
            >
              <option value="cloud">🌐 Cloud - Web only</option>
              <option value="desktop">💻 Desktop - Offline app</option>
              <option value="hybrid">🔄 Hybrid - Cloud + Desktop</option>
            </select>
          </div>

          {/* Duration */}
          <div className="space-y-2">
            <Label htmlFor="durationMonths">
              Duration <span className="text-red-500">*</span>
            </Label>
            <select
              id="durationMonths"
              {...register('durationMonths', { required: true, valueAsNumber: true })}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
            >
              <option value="1">1 month (Trial)</option>
              <option value="1">1 month</option>
              <option value="3">3 months</option>
              <option value="6">6 months</option>
              <option value="12">1 year</option>
              <option value="24">2 years</option>
            </select>
          </div>

          {/* Max Devices (only for desktop/hybrid) */}
          {(selectedLicenseType === 'desktop' || selectedLicenseType === 'hybrid') && (
            <div className="space-y-2">
              <Label htmlFor="maxDevices">Max Devices</Label>
              <Input
                id="maxDevices"
                type="number"
                min="1"
                max="100"
                {...register('maxDevices', { valueAsNumber: true })}
              />
              <p className="text-xs text-muted-foreground">
                Maximum number of devices that can use this license
              </p>
            </div>
          )}

          {/* Auto-generate Key */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="autoGenerateKey"
              checked={autoGenerateKey}
              onChange={(e) => setAutoGenerateKey(e.target.checked)}
              className="w-4 h-4"
            />
            <Label htmlFor="autoGenerateKey" className="cursor-pointer">
              Auto-generate license key
            </Label>
          </div>

          {/* Custom Key */}
          {!autoGenerateKey && (
            <div className="space-y-2">
              <Label htmlFor="customKey">Custom License Key</Label>
              <Input
                id="customKey"
                {...register('customKey')}
                placeholder="LEBIZ-XXXX-XXXX-XXXX-XXXX"
              />
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Admin Notes (Optional)</Label>
            <textarea
              id="notes"
              {...register('notes')}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              placeholder="Internal notes about this license..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createLicense.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createLicense.isPending}>
              {createLicense.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create License'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
